import {
  Account,
  AllDataAccount,
  AllDataDevice,
  Breeding,
  Building,
  Company,
  DeviceWithoutLegacyPassword,
} from "../generated/types";
import {
  AdminRawData,
  AllDataBreeding,
  assembleAdminBreedings,
  fetchAdminRawData,
} from "./breeding";

/**
 * Données croisées des tickets admin / PSC. Un ticket ne reçoit que l'élément qu'il montre et
 * cherche ici ce qui s'y rattache : il est complet quel que soit le ticket ou la page qui l'ouvre.
 */
export type AdminDirectory = {
  accounts: AllDataAccount[];
  devices: AllDataDevice[];
  breedings: AllDataBreeding[];
  buildings: Building[];
};

export const EMPTY_ADMIN_DIRECTORY: AdminDirectory = {
  accounts: [],
  devices: [],
  breedings: [],
  buildings: [],
};

export const buildAdminDirectory = (raw: AdminRawData): AdminDirectory => ({
  accounts: raw.users,
  devices: raw.devices,
  buildings: raw.buildings,
  breedings: assembleAdminBreedings(raw),
});

// Le back renvoie un bâtiment, un élevage ou une entreprise vide (id 0) quand il n'y en a pas.
const present = <T extends { id: number }>(value?: T): T | undefined =>
  value?.id ? value : undefined;

/** Installateurs liés à au moins un de ces produits, chacun une seule fois. */
const linkedInstallers = (entries: AllDataDevice[]): Account[] =>
  entries
    .flatMap((entry) => entry.installers ?? [])
    .map((link) => link.account)
    .filter(
      (account, index, all) =>
        all.findIndex((other) => other.id === account.id) === index
    );

export const deviceLinks = (
  directory: AdminDirectory,
  device: DeviceWithoutLegacyPassword
) => {
  const entry = directory.devices.find((e) => e.device.id === device.id);
  return {
    device: entry?.device ?? device,
    building: present(entry?.building),
    breeding: present(entry?.breeding),
    company: present(entry?.company),
    accounts: (entry?.accounts ?? []).map((link) => link.account),
    installers: (entry?.installers ?? []).map((link) => link.account),
  };
};

export const accountLinks = (directory: AdminDirectory, account: Account) => {
  const entry = directory.accounts.find((e) => e.account.id === account.id);
  return {
    account: entry?.account ?? account,
    company: present(entry?.company),
    breedings: (entry?.breedings ?? []).map((details) => details.breeding),
    devices: entry?.devices ?? [],
  };
};

/** Nom de l'entreprise d'un compte, vide si elle est introuvable. */
export const accountCompanyName = (
  directory: AdminDirectory,
  account: Account
): string => accountLinks(directory, account).company?.company_name ?? "";

export const breedingLinks = (
  directory: AdminDirectory,
  breeding: Breeding
): {
  breeding: Breeding;
  company?: Company;
  buildings: Building[];
  accounts: Account[];
  installers: Account[];
} => {
  const entry = directory.breedings.find((e) => e.breeding.id === breeding.id);
  return {
    breeding: entry?.breeding ?? breeding,
    company: entry?.company,
    buildings: entry?.buildings ?? [],
    accounts: entry?.accounts ?? [],
    installers: linkedInstallers(
      directory.devices.filter((e) => e.breeding?.id === breeding.id)
    ),
  };
};

export const buildingLinks = (
  directory: AdminDirectory,
  building: Building
) => {
  const current =
    directory.buildings.find((b) => b.id === building.id) ?? building;
  const deviceIds = current.device ?? [];
  const parent = directory.breedings.find(
    (e) => e.breeding.id === current.id_breeding
  );
  const deviceEntries = directory.devices.filter((e) =>
    deviceIds.includes(e.device.id)
  );
  return {
    building: current,
    breeding: parent?.breeding,
    company: parent?.company,
    accounts: parent?.accounts ?? [],
    installers: linkedInstallers(deviceEntries),
    devices: deviceEntries.map((e) => e.device),
    // Compté sur les ids du bâtiment : un produit absent de la liste bloque aussi la suppression.
    deviceCount: deviceIds.length,
  };
};

let cache: Promise<AdminDirectory> | null = null;
const listeners = new Set<(directory: AdminDirectory) => void>();

/** Chargé une fois puis partagé ; `refresh` relance la requête et prévient les tickets ouverts. */
export const loadAdminDirectory = (
  refresh = false
): Promise<AdminDirectory> => {
  if (!cache || refresh) {
    const promise = fetchAdminRawData().then(buildAdminDirectory);
    cache = promise;
    promise
      .then((directory) => {
        if (cache === promise) listeners.forEach((notify) => notify(directory));
      })
      .catch(() => {
        if (cache === promise) cache = null;
      });
  }
  return cache;
};

export const subscribeAdminDirectory = (
  listener: (directory: AdminDirectory) => void
) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Pour les tests : oublie les données chargées. */
export const resetAdminDirectory = () => {
  cache = null;
};
